// Turning the API's daily series into what the console shows: window sums,
// deltas against the previous window, weekly buckets for 90-day ranges,
// column heights, and the health strip's thresholds.
// Mirrors the rules in design/archive/handoff/design_handoff_admin_console/README.md.

/** The previous and current windows of a 2×N series. */
export function split(series: readonly number[], days: number): { prev: number[]; cur: number[] } {
  return { prev: series.slice(-2 * days, -days), cur: series.slice(-days) };
}

export const sum = (xs: readonly number[]) => xs.reduce((t, x) => t + x, 0);

export type Tone = "up" | "down" | "flat";

export interface Delta {
  /** "+12", "−3", "±0" (or "+7 pt"). */
  abs: string;
  /** "+24%", "new", "0%" — null for rates (points only). */
  rel: string | null;
  /** The previous value is under 20: the % is noise, read the absolute number. */
  small: boolean;
  tone: Tone;
  prev: number;
}

const sign = (d: number) => (d > 0 ? "+" : d < 0 ? "−" : "±");

export function delta(cur: number, prev: number, unit: "" | " pt" = ""): Delta {
  const d = cur - prev;
  const abs = `${sign(d)}${Math.abs(d).toLocaleString("en-GB")}${unit}`;
  let rel: string | null;
  if (unit === " pt") rel = null;
  else if (prev === 0) rel = cur === 0 ? "0%" : "new";
  else rel = `${sign(d)}${Math.abs(Math.round((d / prev) * 100))}%`;
  return { abs, rel, small: prev > 0 && prev < 20, tone: d > 0 ? "up" : d < 0 ? "down" : "flat", prev };
}

/** 90-day ranges show weekly columns; shorter ranges stay daily. */
export function bucket(values: readonly number[], days: number): number[] {
  if (days <= 30) return [...values];
  const out: number[] = [];
  for (let i = 0; i < values.length; i += 7) out.push(sum(values.slice(i, i + 7)));
  return out;
}

export interface Column {
  /** Height as a % of the chart. */
  h: number;
  /** The previous period's value at the same position, as a % — null when not comparing. */
  p: number | null;
  value: number;
}

/** Column heights on one scale; a non-zero value is never shorter than 4%. */
export function columns(values: readonly number[], previous?: readonly number[] | null): Column[] {
  const max = Math.max(1, ...values, ...(previous ?? []));
  return values.map((v, i) => ({
    h: v ? Math.max(4, (v / max) * 100) : 0,
    p: previous ? ((previous[i] ?? 0) / max) * 100 : null,
    value: v,
  }));
}

export const pct = (a: number, b: number) => (b ? Math.round((a / b) * 100) : 0);

/** Loud health cell: yesterday at or above max(floor, factor × the 7-day average before it). */
export function healthCell(last8: readonly number[], floor: number, factor: number): { value: number; avg: number; loud: boolean } {
  const value = last8[last8.length - 1] ?? 0;
  const avg = Math.round(sum(last8.slice(0, -1)) / 7);
  return { value, avg, loud: value >= Math.max(floor, avg * factor) };
}

/** A tool is "hot" when more than 5% of its calls fail and at least 3 did. */
export const isHot = (calls: number, errors: number) => calls > 0 && errors >= 3 && errors / calls > 0.05;
